import { useCallback, useEffect, useState } from "react";
import { MapPin, Phone } from "lucide-react";
import { api } from "../lib/apiClient";
import { resolveImageUrl } from "../lib/images";

const DAYS = [
    ["monday", "Lundi"],
    ["tuesday", "Mardi"],
    ["wednesday", "Mercredi"],
    ["thursday", "Jeudi"],
    ["friday", "Vendredi"],
    ["saturday", "Samedi"],
    ["sunday", "Dimanche"],
];

function ShopCard({ shop }) {
    return (
        <article
            className="brand-card overflow-hidden"
            data-testid={`shop-card-${shop.slug}`}
        >
            {shop.image_url && (
                <img
                    src={resolveImageUrl(shop.image_url)}
                    alt={shop.name}
                    className="w-full aspect-[4/3] object-cover"
                />
            )}
            <div className="p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <p className="section-tag">boutique</p>
                        <h3 className="font-display text-4xl text-[#2A1408] leading-none">
                            {shop.name}
                        </h3>
                    </div>
                    {shop.opens_soon && (
                        <span className="sticker">
                            {shop.opens_soon_label || "Ouverture prochaine"}
                        </span>
                    )}
                </div>
                <p className="mt-3 flex items-start gap-2 text-[#5C3D2E]">
                    <MapPin size={16} strokeWidth={1.5} className="mt-0.5" /> {shop.address}
                </p>
                {shop.phone && (
                    <p className="mt-2 flex items-center gap-2 text-[#5C3D2E]">
                        <Phone size={16} strokeWidth={2} /> {shop.phone}
                    </p>
                )}

                <div className="mt-5 border-t border-[#E4D1BB] pt-4">
                    <p className="text-xs uppercase tracking-widest text-[#B07B66] mb-2">
                        Horaires
                    </p>
                    <ul className="text-sm text-[#5C3D2E] grid grid-cols-2 grid-rows-4 grid-flow-col gap-x-6 gap-y-1">
                        {DAYS.map(([k, label]) => (
                            <li
                                key={k}
                                className="flex justify-between gap-3 border-b border-dashed border-[#E4D1BB]/60 py-1"
                            >
                                <span>{label}</span>
                                <span className="text-[#3D1F0F]">{shop.hours?.[k] || "-"}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                {shop.map_embed_url && (
                    <iframe
                        title={`Carte ${shop.name}`}
                        src={shop.map_embed_url}
                        className="w-full h-48 rounded-lg mt-5 border border-[#E4D1BB]"
                        loading="lazy"
                    />
                )}
            </div>
        </article>
    );
}

export default function Boutiques() {
    const [shops, setShops] = useState([]);

    const fetchShops = useCallback(async () => {
        try {
            const { data } = await api.get("/shops");
            setShops(data || []);
        } catch (_e) {
            // ignore
        }
    }, []);

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    return (
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20" data-testid="boutiques-page">
            <div className="text-center mb-14 relative">
                <span
                    className="hidden md:inline-block absolute top-2 right-32 sticker"
                    style={{ transform: "rotate(7deg)" }}
                >
                    on vous attend
                </span>
                <p className="section-tag">venez nous voir</p>
                <h1 className="font-display text-7xl md:text-9xl text-[#2A1408] leading-[0.85] mt-1">
                    Nos repaires.
                </h1>
                <p className="mt-3 text-[#5C3D2E] max-w-2xl mx-auto text-lg">
                    Mâcon et Bourg-en-Bresse, l’atelier ouvre ses portes pour toutes
                    les gourmandes et gourmands.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {shops.map((s) => (
                    <ShopCard key={s.id || s.slug} shop={s} />
                ))}
            </div>
        </div>
    );
}
